import React, { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, Layers, Monitor, Download, Share2 } from 'lucide-react'
import { usePresentation } from '../../hooks/usePresentation'
import { Slide } from './Slide'
import { SlideProgress, SlideCounter } from './SlideProgress'
import { SlideThumbnails } from './SlideThumbnails'
import { KeyboardShortcutsModal } from './KeyboardShortcutsModal'
import { PresenterMode } from './PresenterMode'
import { ExportDialog } from './export/ExportDialog'
import { ExportProgress } from './export/ExportProgress'
import { ShareDialog, type ShareData, type ShareConfig } from './share/ShareDialog'
import type { PresentationData } from '../../types/presentation'
import type { ExportOptions } from './export/types'
import { useToast } from '@/hooks/use-toast'

export interface PresentationViewerProps {
  presentation: PresentationData
  className?: string
  accessLevelEditor?: React.ReactNode
  presentationId?: string
}

export const PresentationViewer: React.FC<PresentationViewerProps> = ({
  presentation,
  className,
  accessLevelEditor,
  presentationId,
}) => {
  const { metadata, slides } = presentation
  const { toast } = useToast()

  const [showThumbnails, setShowThumbnails] = useState(false)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [isPresenterMode, setIsPresenterMode] = useState(false)
  const [showExportDialog, setShowExportDialog] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [shareData, setShareData] = useState<ShareData | undefined>(undefined)

  // Export progress state
  const [exportState, setExportState] = useState({
    isExporting: false,
    format: '',
    progress: 0,
    stage: '',
    error: null as string | null,
  })

  const presentationControls = usePresentation({
    totalSlides: slides.length,
    enableKeyboard: true,
  })

  const {
    currentSlide,
    totalSlides,
    isFullscreen,
    transitionType,
    direction,
    nextSlide,
    previousSlide,
    goToSlide,
    toggleFullscreen,
  } = presentationControls

  // Export handler - supports PDF (browser print), HTML (standalone), PPTX (backend)
  const handleExport = async (options: ExportOptions) => {
    const formatDisplay = options.format.toUpperCase()

    // Initialize export state
    setExportState({
      isExporting: true,
      format: formatDisplay,
      progress: 0,
      stage: 'Initializing...',
      error: null,
    })

    try {
      if (options.format === 'pdf') {
        setExportState(prev => ({ ...prev, stage: 'Preparing PDF export...' }))

        // Simulate progress for browser print
        setExportState(prev => ({ ...prev, progress: 30, stage: 'Opening print dialog...' }))
        await new Promise(resolve => setTimeout(resolve, 500))

        // Use browser's print to PDF functionality
        window.print()

        setExportState(prev => ({ ...prev, progress: 100, stage: 'Complete!' }))
        return { success: true }
      } else if (options.format === 'html') {
        setExportState(prev => ({ ...prev, stage: 'Generating HTML...' }))
        await new Promise(resolve => setTimeout(resolve, 300))

        setExportState(prev => ({ ...prev, progress: 20, stage: 'Processing slides...' }))
        await new Promise(resolve => setTimeout(resolve, 300))

        // Dynamically import HTML export utility to avoid loading it upfront
        const { generateHTMLExport, downloadHTML } = await import('@/lib/presentation/export/html')

        setExportState(prev => ({ ...prev, progress: 50, stage: 'Creating standalone file...' }))
        const htmlContent = generateHTMLExport(presentation, {
          includeSlideNumbers: options.includeSlideNumbers,
          includePresenterNotes: options.includeNotes,
        })

        setExportState(prev => ({ ...prev, progress: 80, stage: 'Downloading file...' }))
        await new Promise(resolve => setTimeout(resolve, 200))

        const filename = `${metadata.title || 'presentation'}.html`.replace(/[^a-z0-9]/gi, '_')
        downloadHTML(htmlContent, filename)

        setExportState(prev => ({ ...prev, progress: 100, stage: 'Complete!' }))
        return { success: true }
      } else if (options.format === 'pptx') {
        // Call backend PPTX export endpoint
        if (!presentationId) {
          setExportState(prev => ({ ...prev, error: 'PPTX export is only available for presentations from the library' }))
          return {
            success: false,
            error: 'PPTX export is only available for presentations from the library'
          }
        }

        const token = localStorage.getItem('token')
        if (!token) {
          setExportState(prev => ({ ...prev, error: 'Please log in to export PPTX files' }))
          return {
            success: false,
            error: 'Please log in to export PPTX files'
          }
        }

        setExportState(prev => ({ ...prev, progress: 20, stage: 'Connecting to server...' }))
        await new Promise(resolve => setTimeout(resolve, 300))

        const queryParams = new URLSearchParams({
          includeNotes: options.includeNotes.toString(),
          includeSlideNumbers: options.includeSlideNumbers.toString(),
        })

        setExportState(prev => ({ ...prev, progress: 40, stage: 'Generating presentation...' }))

        const response = await fetch(
          `/api/presentations/${presentationId}/export/pptx?${queryParams}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        setExportState(prev => ({ ...prev, progress: 70, stage: 'Downloading file...' }))

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Export failed' }))
          setExportState(prev => ({ ...prev, error: errorData.message || 'Failed to export PPTX' }))
          return {
            success: false,
            error: errorData.message || 'Failed to export PPTX'
          }
        }

        // Download the PPTX file
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${metadata.title || 'presentation'}.pptx`.replace(/[^a-z0-9]/gi, '_')
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

        setExportState(prev => ({ ...prev, progress: 100, stage: 'Complete!' }))
        return { success: true }
      }
      setExportState(prev => ({ ...prev, error: 'Unknown format' }))
      return { success: false, error: 'Unknown format' }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Export failed'
      setExportState(prev => ({ ...prev, error: errorMessage }))
      return {
        success: false,
        error: errorMessage
      }
    }
  }

  // Share handlers
  const handleCreateShare = async (config: ShareConfig): Promise<ShareData> => {
    if (!presentationId) {
      throw new Error('Cannot share local presentations')
    }

    const token = localStorage.getItem('token')
    if (!token) {
      throw new Error('Please log in to share presentations')
    }

    const response = await fetch(`/api/presentations/${presentationId}/share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(config),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to create share' }))
      throw new Error(errorData.message || 'Failed to create share')
    }

    const data = await response.json()
    return data.share
  }

  const handleUpdateShare = async (shareId: string, config: ShareConfig): Promise<ShareData> => {
    if (!presentationId) {
      throw new Error('Cannot update share for local presentations')
    }

    const token = localStorage.getItem('token')
    if (!token) {
      throw new Error('Please log in to update shares')
    }

    const response = await fetch(`/api/presentations/${presentationId}/shares/${shareId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(config),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to update share' }))
      throw new Error(errorData.message || 'Failed to update share')
    }

    const data = await response.json()
    return data.share
  }

  const handleDeleteShare = async (shareId: string): Promise<void> => {
    if (!presentationId) {
      throw new Error('Cannot delete share for local presentations')
    }

    const token = localStorage.getItem('token')
    if (!token) {
      throw new Error('Please log in to delete shares')
    }

    const response = await fetch(`/api/presentations/${presentationId}/shares/${shareId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to delete share' }))
      throw new Error(errorData.message || 'Failed to delete share')
    }

    setShareData(undefined)
  }

  const handleOpenShareDialog = async () => {
    if (!presentationId) {
      toast({
        title: 'Cannot share local presentations',
        description: 'Only presentations from the library can be shared.',
        variant: 'destructive',
      })
      return
    }

    // Check if share already exists
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast({
          title: 'Authentication required',
          description: 'Please log in to share presentations.',
          variant: 'destructive',
        })
        return
      }

      const response = await fetch(`/api/presentations/${presentationId}/shares`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.shares && data.shares.length > 0) {
          setShareData(data.shares[0])
        }
      }
    } catch (error) {
      // If fetch fails, just proceed without existing share
      console.error('Failed to fetch existing shares:', error)
    }

    setShowShareDialog(true)
  }

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return
      }

      // Exit presenter mode with Escape
      if (e.key === 'Escape' && isPresenterMode) {
        e.preventDefault()
        setIsPresenterMode(false)
        return
      }

      // Toggle presenter mode with D
      if (e.key.toLowerCase() === 'd' && !e.shiftKey) {
        e.preventDefault()
        setIsPresenterMode((prev) => !prev)
        return
      }

      // If presenter mode is active, don't handle other shortcuts
      if (isPresenterMode) {
        return
      }

      // Toggle shortcuts modal with ?
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault()
        setShowShortcuts((prev) => !prev)
        return
      }

      // Toggle thumbnails with T
      if (e.key.toLowerCase() === 't') {
        e.preventDefault()
        setShowThumbnails((prev) => !prev)
        return
      }

      // Close modals with Escape
      if (e.key === 'Escape') {
        if (showThumbnails) {
          setShowThumbnails(false)
        } else if (showShortcuts) {
          setShowShortcuts(false)
        }
        return
      }

      // Don't handle other shortcuts if modals are open
      if (showThumbnails || showShortcuts) {
        return
      }

      // Navigate to first slide with Home
      if (e.key === 'Home') {
        e.preventDefault()
        goToSlide(0)
        return
      }

      // Navigate to last slide with End
      if (e.key === 'End') {
        e.preventDefault()
        goToSlide(totalSlides - 1)
        return
      }

      // Toggle fullscreen with F
      if (e.key.toLowerCase() === 'f') {
        e.preventDefault()
        toggleFullscreen()
        return
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [
    showThumbnails,
    showShortcuts,
    isPresenterMode,
    goToSlide,
    totalSlides,
    toggleFullscreen,
  ])

  const handleSlideSelect = (index: number) => {
    goToSlide(index)
    setShowThumbnails(false)
  }

  return (
    <div className={`relative w-full h-screen overflow-x-hidden overflow-y-auto bg-muted ${className || ''}`}>
      {/* Slide Progress Bar */}
      <SlideProgress current={currentSlide} total={totalSlides} />

      {/* Main Presentation Container */}
      <div className="relative w-full h-full">
        {slides.map((slide, index) => (
          <Slide
            key={slide.id}
            data={slide}
            isActive={index === currentSlide}
            transition={transitionType}
            direction={direction}
          />
        ))}
      </div>

      {/* Slide Counter */}
      <SlideCounter current={currentSlide} total={totalSlides} />

      {/* Navigation Buttons */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-1 sm:gap-2 z-40 px-2">
        <button
          onClick={previousSlide}
          disabled={currentSlide === 0}
          className="p-2 sm:p-3 bg-card/90 backdrop-blur rounded-lg shadow-lg hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
        </button>

        <button
          onClick={() => setShowThumbnails(!showThumbnails)}
          className="p-2 sm:p-3 bg-card/90 backdrop-blur rounded-lg shadow-lg hover:bg-muted transition-all"
          aria-label="Toggle thumbnails"
          title="Toggle thumbnails (T)"
        >
          <Layers className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
        </button>

        <button
          onClick={() => setShowShortcuts(!showShortcuts)}
          className="p-2 sm:p-3 bg-card/90 backdrop-blur rounded-lg shadow-lg hover:bg-muted transition-all"
          aria-label="Show shortcuts"
          title="Keyboard shortcuts (?)"
        >
          <span className="text-base sm:text-lg font-bold text-muted-foreground">?</span>
        </button>

        <button
          onClick={nextSlide}
          disabled={currentSlide === totalSlides - 1}
          className="p-2 sm:p-3 bg-card/90 backdrop-blur rounded-lg shadow-lg hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          aria-label="Next slide"
        >
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
        </button>
      </div>

      {/* Fullscreen Toggle */}
      <button
        onClick={toggleFullscreen}
        className="fixed top-2 right-2 sm:top-4 sm:right-4 p-2 sm:p-3 bg-card/90 backdrop-blur rounded-lg shadow-lg hover:bg-muted transition-all z-40"
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        title="Toggle fullscreen (F)"
      >
        {isFullscreen ? (
          <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
        ) : (
          <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
        )}
      </button>

      {/* Presenter Mode Toggle */}
      <button
        onClick={() => setIsPresenterMode(true)}
        className="hidden sm:flex fixed top-2 sm:top-4 right-12 sm:right-20 p-2 sm:p-3 bg-blue-600/90 hover:bg-blue-700/90 backdrop-blur rounded-lg shadow-lg transition-all z-40 text-white items-center justify-center"
        aria-label="Enter presenter mode"
        title="Enter presenter mode (D)"
      >
        <Monitor className="w-4 h-4 sm:w-5 sm:h-5" />
      </button>

      {/* Export Button - Opens Dialog */}
      <button
        onClick={() => setShowExportDialog(true)}
        className="fixed top-2 right-14 sm:top-4 sm:right-36 p-1.5 sm:p-2 bg-card/90 backdrop-blur rounded-lg shadow-lg hover:bg-muted transition-all z-40"
        aria-label="Export presentation"
        title="Export presentation"
      >
        <Download className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
      </button>

      {/* Share Button */}
      {presentationId && (
        <button
          onClick={handleOpenShareDialog}
          className="fixed top-2 right-[5.5rem] sm:top-4 sm:right-52 p-1.5 sm:p-2 bg-card/90 backdrop-blur rounded-lg shadow-lg hover:bg-muted transition-all z-40"
          aria-label="Share presentation"
          title="Share presentation"
        >
          <Share2 className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
        </button>
      )}

      {/* Presentation Title */}
      {metadata.title && (
        <div className="fixed top-2 left-2 sm:top-4 sm:left-4 px-2 sm:px-4 py-1 sm:py-2 bg-card/90 backdrop-blur rounded-lg shadow-lg text-xs sm:text-sm font-medium text-muted-foreground z-40 max-w-[calc(100vw-12rem)] sm:max-w-md truncate">
          {metadata.title}
        </div>
      )}

      {/* Access Level Editor */}
      {accessLevelEditor && (
        <div className="fixed top-4 right-4 z-40">
          {accessLevelEditor}
        </div>
      )}

      {/* Thumbnails Panel */}
      <SlideThumbnails
        slides={slides}
        currentSlide={currentSlide}
        onSlideSelect={handleSlideSelect}
        isOpen={showThumbnails}
        onClose={() => setShowThumbnails(false)}
      />

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal
        isOpen={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />

      {/* Export Dialog */}
      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        onExport={handleExport}
        presentationTitle={metadata.title || 'presentation'}
      />

      {/* Export Progress Indicator */}
      <ExportProgress
        isExporting={exportState.isExporting}
        format={exportState.format}
        progress={exportState.progress}
        stage={exportState.stage}
        error={exportState.error}
        onCancel={() => setExportState(prev => ({ ...prev, isExporting: false, error: null }))}
        onComplete={() => setExportState(prev => ({ ...prev, isExporting: false }))}
      />

      {/* Share Dialog */}
      {presentationId && (
        <ShareDialog
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          presentationId={presentationId}
          presentationTitle={metadata.title || 'presentation'}
          existingShare={shareData}
          onCreateShare={handleCreateShare}
          onUpdateShare={handleUpdateShare}
          onDeleteShare={handleDeleteShare}
        />
      )}

      {/* Presenter Mode */}
      {isPresenterMode && (
        <PresenterMode
          slides={slides}
          currentSlide={currentSlide}
          onNextSlide={nextSlide}
          onPreviousSlide={previousSlide}
          onClose={() => setIsPresenterMode(false)}
        />
      )}
    </div>
  )
}
